import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import './legal.css';

const Legal = ({ initialTab = 'privacy', onNavigate }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync state if initialTab prop changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="legal-page-wrapper">
      {/* Hero Header */}
      <section className="legal-hero-section py-5">
        <Container className="px-3 px-lg-5">
          <div className="max-w-3xl" style={{ maxWidth: '820px' }}>
            <div className="mb-3">
              <span className="legal-badge">
                <span className="legal-meta-dot dot-orange" />
                LEGAL & COMPLIANCE
              </span>
            </div>

            <h1 className="legal-title mb-3">
              {activeTab === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
            </h1>

            <p className="legal-subtitle mb-4">
              {activeTab === 'privacy'
                ? 'MSquare Professionals Pvt. Ltd. is committed to protecting your privacy. This policy explains how we collect, use, store, and protect your personal data with transparency and care.'
                : 'Please read these terms carefully before using our website or engaging our services. By proceeding, you agree to be bound by these Terms of Service.'}
            </p>

            <div className="d-flex flex-wrap gap-4 align-items-center mb-4">
              <div className="legal-meta-tag">
                <span className="legal-meta-dot dot-orange" />
                <span>Effective Date: <strong className="text-body">June 1, 2025</strong></span>
              </div>
              <div className="legal-meta-tag">
                <span className="legal-meta-dot dot-blue" />
                <span>Last Updated: <strong className="text-body">June 1, 2025</strong></span>
              </div>
            </div>

            {/* Tab switch between Privacy Policy and Terms of Service */}
            <div className="legal-tabs-bar ms-0 mt-3">
              <button
                type="button"
                className={`legal-tab-btn ${activeTab === 'privacy' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('privacy');
                  if (onNavigate) window.location.hash = 'privacy';
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Privacy Policy
              </button>
              <button
                type="button"
                className={`legal-tab-btn ${activeTab === 'terms' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('terms');
                  if (onNavigate) window.location.hash = 'terms';
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Terms of Service
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* Main Body */}
      <section className="py-5">
        <Container className="px-3 px-lg-5">
          <div className="legal-layout-container">
            {/* Article Content */}
            <article className="legal-article-col">
              {activeTab === 'privacy' ? (
                /* ================= PRIVACY POLICY CONTENT ================= */
                <div>
                  <section id="overview" className="legal-section-block">
                    <h2 className="legal-section-heading">Overview</h2>
                    <p className="legal-section-text">
                      This Privacy Policy describes how MSquare Professionals Pvt. Ltd. (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) collects, uses, stores, and protects information about you when you visit our website at msquareprofessionals.com, use our services, or communicate with us. By accessing our website or engaging our services, you agree to the practices described in this policy.
                    </p>
                  </section>

                  <section id="information-collected" className="legal-section-block">
                    <h2 className="legal-section-heading">Information We Collect</h2>
                    <p className="legal-section-text">
                      We collect information to deliver high-performance growth infrastructure, marketing execution, and custom technology solutions:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Contact & Identity Data</div>
                        <p className="legal-info-card-desc">
                          Name, email address, phone number, and company name submitted through contact forms, project enquiry forms, or newsletter subscriptions.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Business Information</div>
                        <p className="legal-info-card-desc">
                          Details about your business, industry, project requirements, and budget shared during the enquiry or onboarding process.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Usage & Technical Data</div>
                        <p className="legal-info-card-desc">
                          IP address, browser type, operating system, pages visited, time on site, and referring URLs — collected automatically via server logs and analytics tools.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Communications Data</div>
                        <p className="legal-info-card-desc">
                          Emails, messages, meeting notes, and other correspondence exchanged between you and our team.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Payment Information</div>
                        <p className="legal-info-card-desc">
                          Billing details processed through our payment partners. We do not store card numbers or sensitive payment credentials directly.
                        </p>
                      </div>
                    </div>
                    <div className="legal-callout-banner">
                      <span className="legal-callout-icon">ⓘ</span>
                      <p className="legal-callout-text">
                        We do not sell, rent, or trade your personal data to third parties under any circumstances.
                      </p>
                    </div>
                  </section>

                  <section id="how-we-use" className="legal-section-block">
                    <h2 className="legal-section-heading">How We Use Your Information</h2>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Service Delivery</div>
                        <p className="legal-info-card-desc">
                          To respond to enquiries, onboard clients, and deliver the services you have engaged us for.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Communication</div>
                        <p className="legal-info-card-desc">
                          To send project updates, invoices, and service-related notifications.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Marketing & Insights</div>
                        <p className="legal-info-card-desc">
                          To send newsletters or growth insights — only with your consent. You may unsubscribe at any time.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Improvement & Analytics</div>
                        <p className="legal-info-card-desc">
                          To analyse website usage and improve our services, content, and user experience.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Legal Compliance</div>
                        <p className="legal-info-card-desc">
                          To comply with applicable laws, regulations, and legal obligations.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Security & Abuse Prevention</div>
                        <p className="legal-info-card-desc">
                          To detect, prevent, and address fraud, abuse, or security incidents across our infrastructure.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section id="data-sharing" className="legal-section-block">
                    <h2 className="legal-section-heading">Data Sharing & Disclosure</h2>
                    <p className="legal-section-text">
                      We do not sell your data. We share your information only in the following limited circumstances:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Service Providers</div>
                        <p className="legal-info-card-desc">
                          Trusted third-party vendors (hosting, analytics, email delivery, payment processing) who process data on our behalf under strict data processing agreements.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Legal Requirements</div>
                        <p className="legal-info-card-desc">
                          When required by law, court order, or government authority, or to protect our legal rights and the safety of others.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Business Transfers</div>
                        <p className="legal-info-card-desc">
                          In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction with prior notice.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section id="cookies" className="legal-section-block">
                    <h2 className="legal-section-heading">Cookies & Tracking</h2>
                    <p className="legal-section-text">
                      Our website uses cookies and local storage tokens to deliver enhanced interactive experiences:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Essential Cookies</div>
                        <p className="legal-info-card-desc">
                          Required for the website to function correctly (e.g., remembering your dark/light theme preference). Cannot be disabled.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Analytics Cookies</div>
                        <p className="legal-info-card-desc">
                          Used to understand how visitors interact with our site (e.g., page views, session duration, device viewport dimensions). Collected only with consent.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Marketing Cookies</div>
                        <p className="legal-info-card-desc">
                          Used to display relevant campaigns and measure performance. Activated only with your explicit consent.
                        </p>
                      </div>
                    </div>
                    <div className="legal-callout-banner">
                      <span className="legal-callout-icon">ⓘ</span>
                      <p className="legal-callout-text">
                        You can manage or disable cookies through your browser settings at any time. Disabling essential cookies may affect site functionality.
                      </p>
                    </div>
                  </section>

                  <section id="data-retention" className="legal-section-block">
                    <h2 className="legal-section-heading">Data Retention</h2>
                    <p className="legal-section-text">
                      We retain personal data only as long as necessary to satisfy the purpose for which it was collected:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Enquiry / Contact Form Data</div>
                        <p className="legal-info-card-desc">Retained for up to 3 years from the date of submission.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Client Project Data</div>
                        <p className="legal-info-card-desc">Retained for the duration of the engagement plus 5 years for legal and audit purposes.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Newsletter Subscribers</div>
                        <p className="legal-info-card-desc">Retained until you unsubscribe. Removed within 30 days of unsubscription.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Analytics Data</div>
                        <p className="legal-info-card-desc">Aggregated and anonymised data may be retained indefinitely.</p>
                      </div>
                    </div>
                  </section>

                  <section id="your-rights" className="legal-section-block">
                    <h2 className="legal-section-heading">Your Rights</h2>
                    <p className="legal-section-text">
                      Depending on your jurisdiction, you have explicit rights regarding your personal data. To exercise any right, email us at <a href="mailto:info@msquareprofessionals.com" className="text-orange">info@msquareprofessionals.com</a> and our team will respond within 30 days.
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Right to Access</div>
                        <p className="legal-info-card-desc">Request a copy of the personal data we hold about you.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Right to Rectification</div>
                        <p className="legal-info-card-desc">Request correction of inaccurate or incomplete personal records.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Right to Erasure</div>
                        <p className="legal-info-card-desc">Request deletion of your personal data (&quot;right to be forgotten&quot;), subject to legal obligations.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Right to Restriction</div>
                        <p className="legal-info-card-desc">Request that we restrict processing of your data in certain circumstances.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Right to Portability</div>
                        <p className="legal-info-card-desc">Receive your data in a structured, commonly used machine-readable format.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Right to Object & Withdraw Consent</div>
                        <p className="legal-info-card-desc">Object to processing for direct marketing or revoke previously granted consent at any time.</p>
                      </div>
                    </div>
                  </section>

                  <section id="security" className="legal-section-block">
                    <h2 className="legal-section-heading">Data Security</h2>
                    <p className="legal-section-text">
                      We implement enterprise-grade technical and organisational security measures to protect your data:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Encryption</div>
                        <p className="legal-info-card-desc">All data transmitted between your browser and our servers is encrypted using modern TLS/SSL.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Access Controls</div>
                        <p className="legal-info-card-desc">Data access is restricted to authorised personnel on a need-to-know basis with role-based policies.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Secure Infrastructure</div>
                        <p className="legal-info-card-desc">Our systems are hosted on enterprise-grade, ISO-certified cloud infrastructure.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Regular Audits</div>
                        <p className="legal-info-card-desc">We conduct periodic security reviews and vulnerability assessments.</p>
                      </div>
                    </div>
                    <div className="legal-callout-banner">
                      <span className="legal-callout-icon">ⓘ</span>
                      <p className="legal-callout-text">
                        No method of internet transmission is 100% secure. While we employ industry best practices, we cannot guarantee absolute security.
                      </p>
                    </div>
                  </section>

                  <section id="third-party" className="legal-section-block">
                    <h2 className="legal-section-heading">Third-Party Links & Services</h2>
                    <p className="legal-section-text">
                      Our website may contain links to third-party websites or integrate with external platforms. We are not responsible for the privacy practices or content of those third parties. We encourage you to review the privacy policies of any third-party services you use.
                    </p>
                  </section>

                  <section id="childrens-privacy" className="legal-section-block">
                    <h2 className="legal-section-heading">Children&apos;s Privacy</h2>
                    <p className="legal-section-text">
                      Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal data from minors. If you believe we have inadvertently collected data from a minor, please contact us immediately at <a href="mailto:info@msquareprofessionals.com" className="text-orange">info@msquareprofessionals.com</a> and we will delete it promptly.
                    </p>
                  </section>

                  <section id="grievance" className="legal-section-block">
                    <h2 className="legal-section-heading">Grievance Officer</h2>
                    <p className="legal-section-text">
                      In accordance with the Information Technology Act, 2000 and the rules made thereunder, the name and contact details of the Grievance Officer are:
                    </p>
                    <div className="legal-info-card">
                      <div className="legal-info-card-title">Grievance Officer</div>
                      <p className="legal-info-card-desc mb-1">MSquare Professionals Pvt. Ltd.</p>
                      <p className="legal-info-card-desc mb-2">SCO 40, 4th Floor, Civil Line, Sector 15, Gurugram, Haryana 122002, India</p>
                      <a href="mailto:info@msquareprofessionals.com" className="text-orange fw-semibold">info@msquareprofessionals.com</a>
                    </div>
                    <div className="legal-callout-banner">
                      <span className="legal-callout-icon">ⓘ</span>
                      <p className="legal-callout-text">
                        We will address your grievance within 30 days of receipt.
                      </p>
                    </div>
                  </section>

                  <section id="governing-law" className="legal-section-block">
                    <h2 className="legal-section-heading">Governing Law & Jurisdiction</h2>
                    <p className="legal-section-text">
                      This Privacy Policy is governed by the laws of India, including the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011. Any disputes arising from this policy shall be subject to the exclusive jurisdiction of the courts of Gurugram, Haryana.
                    </p>
                  </section>

                  <section id="changes" className="legal-section-block">
                    <h2 className="legal-section-heading">Changes to This Policy</h2>
                    <p className="legal-section-text">
                      We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. We will notify you of material changes by updating the &quot;Last Updated&quot; date at the top of this page and, where appropriate, via email. Continued use of our services after changes constitutes acceptance of the updated policy.
                    </p>
                  </section>

                  {/* Contact Help Box */}
                  <div className="legal-contact-box">
                    <h3>Questions About Privacy?</h3>
                    <p>If you have any questions about this Privacy Policy or how we handle your data, our compliance team is here to assist:</p>
                    <div className="legal-contact-details">
                      <strong>MSquare Professionals Pvt. Ltd.</strong>
                      <span>SCO 40, 4th Floor, Civil Line, Sector 15, Gurugram, Haryana 122002, India</span>
                      <a href="mailto:info@msquareprofessionals.com">info@msquareprofessionals.com</a>
                    </div>
                  </div>
                </div>
              ) : (
                /* ================= TERMS OF SERVICE CONTENT ================= */
                <div>
                  <section id="acceptance" className="legal-section-block">
                    <h2 className="legal-section-heading">Acceptance of Terms</h2>
                    <p className="legal-section-text">
                      By accessing or using the MSquare Professionals Pvt. Ltd. website at msquareprofessionals.com, engaging our services, or submitting an enquiry, you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, please do not use our website or services. For questions, contact us at <a href="mailto:info@msquareprofessionals.com" className="text-orange">info@msquareprofessionals.com</a>.
                    </p>
                  </section>

                  <section id="services" className="legal-section-block">
                    <h2 className="legal-section-heading">Description of Services</h2>
                    <p className="legal-section-text">
                      MSquare Professionals Pvt. Ltd. is a Business Growth Infrastructure Company providing integrated Growth Marketing, Business Automation, and Technology Solutions. Our services include but are not limited to:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Growth Marketing</div>
                        <p className="legal-info-card-desc">
                          Performance marketing, lead generation, SEO & SEM, social media marketing, and branding & digital strategy.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Business Automation</div>
                        <p className="legal-info-card-desc">
                          CRM solutions, lead management systems, workflow automation, sales automation, attendance management systems, and process digitization.
                        </p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Technology Solutions</div>
                        <p className="legal-info-card-desc">
                          Custom software development, web applications, mobile applications, website design & development, enterprise software solutions, and API integrations.
                        </p>
                      </div>
                    </div>
                    <div className="legal-callout-banner">
                      <span className="legal-callout-icon">ⓘ</span>
                      <p className="legal-callout-text">
                        Specific deliverables, timelines, scope, and fees are governed by individual service agreements or Statements of Work (&quot;SOW&quot;) signed between the Company and the client.
                      </p>
                    </div>
                  </section>

                  <section id="client-obligations" className="legal-section-block">
                    <h2 className="legal-section-heading">Client Responsibilities</h2>
                    <p className="legal-section-text">Clients engaging our services agree to:</p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Timely Cooperation</div>
                        <p className="legal-info-card-desc">Provide accurate, complete, and timely information, assets, feedback, and approvals necessary for project delivery.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Payment Compliance</div>
                        <p className="legal-info-card-desc">Make payments in accordance with agreed schedules as outlined in the SOW or invoice.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Legal Content</div>
                        <p className="legal-info-card-desc">Ensure all content, assets, branding, and materials provided to us do not infringe any third-party intellectual property, privacy, or other legal rights.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Authorised Representative</div>
                        <p className="legal-info-card-desc">Ensure that the individual engaging our services has authority to bind the organisation to these Terms.</p>
                      </div>
                    </div>
                  </section>

                  <section id="use-of-website" className="legal-section-block">
                    <h2 className="legal-section-heading">Acceptable Use of Website</h2>
                    <p className="legal-section-text">You agree not to use our website or services to:</p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Unlawful Activities</div>
                        <p className="legal-info-card-desc">Engage in any activity that violates applicable laws, regulations, or third-party rights.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Unauthorised Access</div>
                        <p className="legal-info-card-desc">Attempt to gain unauthorised access to our systems, servers, databases, or any connected networks.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Harmful Content</div>
                        <p className="legal-info-card-desc">Transmit any harmful, offensive, defamatory, fraudulent, or otherwise objectionable content.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Data Scraping</div>
                        <p className="legal-info-card-desc">Scrape, harvest, or systematically collect data from our website without our prior written consent.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">System Disruption</div>
                        <p className="legal-info-card-desc">Introduce malware, viruses, or any code designed to disrupt, damage, or impair our systems or services.</p>
                      </div>
                    </div>
                  </section>

                  <section id="intellectual-property" className="legal-section-block">
                    <h2 className="legal-section-heading">Intellectual Property</h2>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Company IP</div>
                        <p className="legal-info-card-desc">All content on this website — including text, graphics, logos, design elements, and code — is the property of MSquare Professionals Pvt. Ltd. or its licensors, protected by applicable copyright and trademark laws.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Client Deliverables</div>
                        <p className="legal-info-card-desc">Upon receipt of full payment, clients receive a licence to use deliverables produced under a SOW as specified therein. Ownership transfer (if applicable) will be explicitly stated in the SOW.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Portfolio Rights</div>
                        <p className="legal-info-card-desc">MSquare retains the right to display completed work in its portfolio, case studies, and marketing materials unless otherwise agreed in writing.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Client Assets</div>
                        <p className="legal-info-card-desc">All assets, data, and intellectual property provided by the client remain the property of the client.</p>
                      </div>
                    </div>
                  </section>

                  <section id="payment" className="legal-section-block">
                    <h2 className="legal-section-heading">Payment Terms</h2>
                    <p className="legal-section-text">Payment schedules are defined in individual SOWs or invoices. Unless otherwise stated:</p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Payment Due</div>
                        <p className="legal-info-card-desc">Invoices are due within 15 days of issue.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Late Payment</div>
                        <p className="legal-info-card-desc">Late payments may attract a fee of 2% per month on the outstanding balance from the due date.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Work Suspension</div>
                        <p className="legal-info-card-desc">MSquare reserves the right to suspend work on overdue accounts after providing written notice.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Taxes</div>
                        <p className="legal-info-card-desc">All fees are exclusive of applicable taxes (GST, TDS, etc.) unless explicitly stated. Taxes will be applied as required by law.</p>
                      </div>
                    </div>
                  </section>

                  <section id="confidentiality" className="legal-section-block">
                    <h2 className="legal-section-heading">Confidentiality</h2>
                    <p className="legal-section-text">
                      Both parties agree to maintain the confidentiality of non-public business information, trade secrets, technical data, pricing, and client information disclosed during the engagement:
                    </p>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Duration</div>
                        <p className="legal-info-card-desc">Survives termination of the service relationship by two (2) years.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Exceptions</div>
                        <p className="legal-info-card-desc">Does not apply to information that is publicly available, independently developed, or required to be disclosed by law.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">NDA Priority</div>
                        <p className="legal-info-card-desc">Where a separate Non-Disclosure Agreement (NDA) is executed, its terms shall govern over this clause.</p>
                      </div>
                    </div>
                  </section>

                  <section id="warranties" className="legal-section-block">
                    <h2 className="legal-section-heading">Warranties & Disclaimers</h2>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Website &quot;As Is&quot;</div>
                        <p className="legal-info-card-desc">The website and its content are provided &quot;as is&quot; without warranties of any kind, express or implied, including but not limited to merchantability, fitness for a particular purpose, or non-infringement.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">No Guaranteed Results</div>
                        <p className="legal-info-card-desc">We do not guarantee specific business results (revenue, rankings, leads, ROI) from our services. All projections and estimates are based on historical data and industry benchmarks and are not binding commitments.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Service Availability</div>
                        <p className="legal-info-card-desc">We do not warrant that the website will be error-free, uninterrupted, or free of viruses or other harmful components.</p>
                      </div>
                    </div>
                  </section>

                  <section id="liability" className="legal-section-block">
                    <h2 className="legal-section-heading">Limitation of Liability</h2>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Exclusion of Indirect Damages</div>
                        <p className="legal-info-card-desc">MSquare shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising from your use of our services or website.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Liability Cap</div>
                        <p className="legal-info-card-desc">Our total aggregate liability to any client shall not exceed the total fees paid by that client to MSquare in the three (3) months immediately preceding the claim.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Force Majeure</div>
                        <p className="legal-info-card-desc">We are not liable for delays or failures resulting from circumstances beyond our reasonable control, including natural disasters, government actions, internet outages, or third-party service failures.</p>
                      </div>
                    </div>
                  </section>

                  <section id="termination" className="legal-section-block">
                    <h2 className="legal-section-heading">Termination</h2>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Notice Period</div>
                        <p className="legal-info-card-desc">Either party may terminate a service engagement with 30 days written notice, unless a different notice period is specified in the SOW.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Immediate Termination</div>
                        <p className="legal-info-card-desc">MSquare may terminate immediately for material breach, non-payment exceeding 30 days, or conduct that violates these Terms.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Upon Termination</div>
                        <p className="legal-info-card-desc">The client is responsible for payment of all work completed and expenses incurred up to the effective termination date. Non-refundable deposits or advance payments shall be retained by MSquare.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Data Handover</div>
                        <p className="legal-info-card-desc">Upon termination, both parties will cooperate in good faith to transfer any client-owned assets, data, or deliverables within 15 business days.</p>
                      </div>
                    </div>
                  </section>

                  <section id="dispute-resolution" className="legal-section-block">
                    <h2 className="legal-section-heading">Dispute Resolution</h2>
                    <div className="legal-grid-cards">
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Good Faith Negotiation</div>
                        <p className="legal-info-card-desc">Both parties agree to attempt to resolve any dispute through good-faith negotiation within 30 days of written notice of the dispute.</p>
                      </div>
                      <div className="legal-info-card">
                        <div className="legal-info-card-title">Mediation & Arbitration</div>
                        <p className="legal-info-card-desc">If negotiation fails, unresolved disputes may be referred to arbitration in accordance with the Arbitration and Conciliation Act, 1996.</p>
                      </div>
                    </div>
                  </section>

                  <section id="governing-law" className="legal-section-block">
                    <h2 className="legal-section-heading">Governing Law & Jurisdiction</h2>
                    <p className="legal-section-text">
                      These Terms of Service are governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Gurugram, Haryana, India.
                    </p>
                  </section>

                  <section id="changes" className="legal-section-block">
                    <h2 className="legal-section-heading">Changes to These Terms</h2>
                    <p className="legal-section-text">
                      We reserve the right to update these Terms at any time. Material changes will be communicated by updating the &quot;Last Updated&quot; date on this page and, where appropriate, by email notification. Continued use of our services after changes constitutes your acceptance of the updated Terms.
                    </p>
                  </section>

                  {/* Contact Help Box */}
                  <div className="legal-contact-box">
                    <h3>Questions About Terms of Service?</h3>
                    <p>If you have any questions regarding these Terms or our contractual framework, reach out to us:</p>
                    <div className="legal-contact-details">
                      <strong>MSquare Professionals Pvt. Ltd.</strong>
                      <span>SCO 40, 4th Floor, Civil Line, Sector 15, Gurugram, Haryana 122002, India</span>
                      <a href="mailto:info@msquareprofessionals.com">info@msquareprofessionals.com</a>
                    </div>
                  </div>
                </div>
              )}
            </article>

            {/* Sidebar Quick-Jump Table of Contents */}
            <aside className="legal-sidebar-col" aria-label="Table of contents">
              <div className="legal-toc-title">On This Page</div>
              {activeTab === 'privacy' ? (
                <ul className="legal-toc-list">
                  <li><a href="#overview" onClick={(e) => scrollToSection(e, 'overview')} className="legal-toc-link">Overview</a></li>
                  <li><a href="#information-collected" onClick={(e) => scrollToSection(e, 'information-collected')} className="legal-toc-link">Information We Collect</a></li>
                  <li><a href="#how-we-use" onClick={(e) => scrollToSection(e, 'how-we-use')} className="legal-toc-link">How We Use Information</a></li>
                  <li><a href="#data-sharing" onClick={(e) => scrollToSection(e, 'data-sharing')} className="legal-toc-link">Data Sharing</a></li>
                  <li><a href="#cookies" onClick={(e) => scrollToSection(e, 'cookies')} className="legal-toc-link">Cookies & Tracking</a></li>
                  <li><a href="#data-retention" onClick={(e) => scrollToSection(e, 'data-retention')} className="legal-toc-link">Data Retention</a></li>
                  <li><a href="#your-rights" onClick={(e) => scrollToSection(e, 'your-rights')} className="legal-toc-link">Your Rights</a></li>
                  <li><a href="#security" onClick={(e) => scrollToSection(e, 'security')} className="legal-toc-link">Data Security</a></li>
                  <li><a href="#third-party" onClick={(e) => scrollToSection(e, 'third-party')} className="legal-toc-link">Third-Party Links</a></li>
                  <li><a href="#childrens-privacy" onClick={(e) => scrollToSection(e, 'childrens-privacy')} className="legal-toc-link">Children&apos;s Privacy</a></li>
                  <li><a href="#grievance" onClick={(e) => scrollToSection(e, 'grievance')} className="legal-toc-link">Grievance Officer</a></li>
                  <li><a href="#governing-law" onClick={(e) => scrollToSection(e, 'governing-law')} className="legal-toc-link">Governing Law</a></li>
                  <li><a href="#changes" onClick={(e) => scrollToSection(e, 'changes')} className="legal-toc-link">Policy Changes</a></li>
                </ul>
              ) : (
                <ul className="legal-toc-list">
                  <li><a href="#acceptance" onClick={(e) => scrollToSection(e, 'acceptance')} className="legal-toc-link">Acceptance of Terms</a></li>
                  <li><a href="#services" onClick={(e) => scrollToSection(e, 'services')} className="legal-toc-link">Description of Services</a></li>
                  <li><a href="#client-obligations" onClick={(e) => scrollToSection(e, 'client-obligations')} className="legal-toc-link">Client Responsibilities</a></li>
                  <li><a href="#use-of-website" onClick={(e) => scrollToSection(e, 'use-of-website')} className="legal-toc-link">Acceptable Use</a></li>
                  <li><a href="#intellectual-property" onClick={(e) => scrollToSection(e, 'intellectual-property')} className="legal-toc-link">Intellectual Property</a></li>
                  <li><a href="#payment" onClick={(e) => scrollToSection(e, 'payment')} className="legal-toc-link">Payment Terms</a></li>
                  <li><a href="#confidentiality" onClick={(e) => scrollToSection(e, 'confidentiality')} className="legal-toc-link">Confidentiality</a></li>
                  <li><a href="#warranties" onClick={(e) => scrollToSection(e, 'warranties')} className="legal-toc-link">Warranties & Disclaimers</a></li>
                  <li><a href="#liability" onClick={(e) => scrollToSection(e, 'liability')} className="legal-toc-link">Limitation of Liability</a></li>
                  <li><a href="#termination" onClick={(e) => scrollToSection(e, 'termination')} className="legal-toc-link">Termination</a></li>
                  <li><a href="#dispute-resolution" onClick={(e) => scrollToSection(e, 'dispute-resolution')} className="legal-toc-link">Dispute Resolution</a></li>
                  <li><a href="#governing-law" onClick={(e) => scrollToSection(e, 'governing-law')} className="legal-toc-link">Governing Law</a></li>
                  <li><a href="#changes" onClick={(e) => scrollToSection(e, 'changes')} className="legal-toc-link">Changes to Terms</a></li>
                </ul>
              )}

              <div className="legal-toc-divider" />
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 text-start small legal-toc-link"
                onClick={() => {
                  setActiveTab(activeTab === 'privacy' ? 'terms' : 'privacy');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                {activeTab === 'privacy' ? '→ View Terms of Service' : '→ View Privacy Policy'}
              </button>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('contact');
                }}
                className="legal-toc-link small d-block mt-2"
              >
                → Contact Team
              </a>
            </aside>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Legal;
